import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";

export type Campaign = { id: string; name: string; state: string | null; status: string; is_large: boolean; timezone: string | null;
  /** Whether the field app offers sign/place capture (migration 0279). Off by default: most
   *  campaigns never place a highway sign, and a canvasser knocking doors should not be handed
   *  a button that has nothing to do with the job. */
  places_enabled?: boolean;
  /** Whether this account carries voter data at all (migration 0307). A signs-only account -- a
   *  sign vendor licensed for a county -- gets the sign tools and no voters, no walk turf. The
   *  database is the enforcement; this flag is only so the screen doesn't offer what it can't do.
   *  Undefined is treated as TRUE: every campaign that existed before 0307 has voters. */
  voters_enabled?: boolean;
  /** WHICH AREAS OF THE DASHBOARD THIS PERSON MAY OPEN HERE (migration 0314). Rides on the
   *  campaign rather than the user because it is per membership -- the same person can run signs
   *  only on one campaign and everything on another, and the switcher already fetches this.
   *
   *  EMPTY MEANS EVERYTHING THEIR RANK ALLOWS, which is what every membership held the day it
   *  shipped. Undefined means the same: a fence that defaults to ON locks out every existing
   *  manager, and the failure would be a morning, not a test. */
  sections?: string[];
  /** WHETHER THIS PERSON OUTRANKS A CANVASSER ON THIS CAMPAIGN (0319). Manners, not permission:
   *  every manager-gated endpoint refuses on its own, and this only decides whether a control
   *  that would be refused is drawn at all. Rides on the campaign because rank is per membership
   *  -- the same person can be a manager on one campaign and crew on another. Undefined is
   *  treated as FALSE: drawing a control somebody cannot use is worse than leaving it out. */
  can_manage?: boolean;
  /** HOW THIS CAMPAIGN CALLS AND TEXTS (0358): "free" (the volunteer's own phone) or "paid" (the
   *  campaign's number through Orator). NULL until a manager answers the picker on Calls / Texts --
   *  which is exactly what decides whether that page opens on the picker or on the lists. */
  calls_mode?: "free" | "paid" | null;
  texts_mode?: "free" | "paid" | null };
type CampaignState = {
  campaigns: Campaign[];
  current: Campaign | null;
  setCurrent: (c: Campaign) => void;
  refresh: () => Promise<void>;
  loading: boolean;
  error: string | null;
};
const KEY = "orator.campaign";
const Ctx = createContext<CampaignState>({ campaigns: [], current: null, setCurrent: () => {}, refresh: async () => {}, loading: true, error: null });

export function CampaignProvider({ children }: { children: ReactNode }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [current, setCurrentState] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    // The API returns only campaigns the signed-in user belongs to (member-scoped, RLS-backed).
    try {
      const { campaigns } = await api<{ campaigns: Campaign[] }>("/campaigns");
      const list = campaigns ?? [];
      setCampaigns(list);
      setCurrentState((prev) => {
        const saved = localStorage.getItem(KEY);
        return list.find((c) => c.id === (prev?.id ?? saved)) ?? list[0] ?? null;
      });
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const setCurrent = (c: Campaign) => { localStorage.setItem(KEY, c.id); setCurrentState(c); };
  return <Ctx.Provider value={{ campaigns, current, setCurrent, refresh: load, loading, error }}>{children}</Ctx.Provider>;
}

export const useCampaign = () => useContext(Ctx);
